/**
 * Syncs Advanced Commerce product cards with theme product.json:
 * - low-stock badges
 * - Set of 2 unit label (tag or custom.is_set_of_2 pair SKUs)
 *
 * Autocomplete: GrapheneHC rebuilds `.acs_auto_prod` on every keystroke.
 * Apply cached flags synchronously so "Set of 2" does not flicker empty→fill.
 */
(function () {
  /** @type {Map<string, Promise<{low_stock: boolean, is_set_of_2: boolean}>>} */
  const pendingFetches = new Map();
  /** @type {Map<string, {low_stock: boolean, is_set_of_2: boolean}>} */
  const resolvedFlags = new Map();
  let debounceTimer;
  let isApplying = false;

  const CARD_SELECTORS = [
    '.acs_auto_prod',
    '.product-card-wrapper',
    '.grid__item .card-wrapper',
    '#product-grid .grid__item',
    '#complementaryProducts .product__slide',
  ].join(',');

  const OBSERVE_SELECTORS = [
    '.acs_auto_popup',
    '.acs_collection_results',
    '.acs_results',
    '#product-grid',
    '#complementaryProducts',
  ];

  const THEME_NODE_SELECTOR = '.prd-Price_each_item, .prd-Card_Messaging, .js-LowStockMessaging';

  const getHandleFromCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  const fetchProductFlags = (handle) => {
    if (resolvedFlags.has(handle)) {
      return Promise.resolve(resolvedFlags.get(handle));
    }
    if (pendingFetches.has(handle)) return pendingFetches.get(handle);

    const promise = (async () => {
      try {
        const response = await fetch(`/products/${handle}?view=json`, { method: 'GET' });
        if (!response.ok) {
          const empty = { low_stock: false, is_set_of_2: false };
          resolvedFlags.set(handle, empty);
          return empty;
        }
        const data = await response.json();
        const flags = {
          low_stock: data?.low_stock === true,
          is_set_of_2: data?.is_set_of_2 === true || data?.is_set_of_2_pair === true,
        };
        resolvedFlags.set(handle, flags);
        return flags;
      } catch (_) {
        const empty = { low_stock: false, is_set_of_2: false };
        resolvedFlags.set(handle, empty);
        return empty;
      } finally {
        pendingFetches.delete(handle);
      }
    })();

    pendingFetches.set(handle, promise);
    return promise;
  };

  const getBadgeHost = (card) =>
    card.querySelector('.card__badge') ||
    card.querySelector('.acs_auto_prod_image') ||
    card.querySelector('.card__media') ||
    card;

  const applyLowStockBadge = (card, message) => {
    if (card.querySelector('.prd-Card_Urgency')) return;
    const host = getBadgeHost(card);
    if (!host) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'prd-Card_Messaging js-LowStockMessaging';
    wrapper.innerHTML = `<div class="prd-Card_Urgency">${message}</div>`;
    host.appendChild(wrapper);
  };

  /**
   * GrapheneHC often labels dining chairs "each" unless the Set of 2 tag is indexed.
   * Pair metafield SKUs need the same "Set of 2" unit label as legacy tagged products.
   * Append inside `.price__container` when present so `|` (prd-Price_each_item::before)
   * matches PLP / price.liquid — not as a sibling of `.price__stack`.
   */
  const applySetOf2UnitLabel = (card) => {
    const priceRoot =
      card.querySelector('.acs_auto_prod_price') ||
      card.querySelector('.price') ||
      card.querySelector('.card-information') ||
      card;

    let unitEl = priceRoot.querySelector('.prd-Price_each_item');
    if (!unitEl) {
      unitEl = document.createElement('span');
      unitEl.className = 'prd-Price_each_item';
      unitEl.innerHTML = '<span>Set of 2</span>';
      const container =
        priceRoot.querySelector('.price__container') ||
        priceRoot.querySelector('.price .price__container');
      (container || priceRoot).appendChild(unitEl);
      return;
    }

    unitEl.classList.remove('each');
    const label = (unitEl.textContent || '').replace(/\s+/g, ' ').trim();
    if (!/^set of 2$/i.test(label)) {
      unitEl.innerHTML = '<span>Set of 2</span>';
    }

    /* Move mis-parented labels into price__container (Pair with / AC cards) */
    const container = unitEl.closest('.price')?.querySelector('.price__container');
    if (container && unitEl.parentElement !== container) {
      container.appendChild(unitEl);
    }
  };

  const applyFlagsToCard = (card, flags) => {
    if (!flags) return;
    const message = window.theme?.urgency_message;

    isApplying = true;
    try {
      if (flags.low_stock && message) {
        applyLowStockBadge(card, message);
      }
      if (flags.is_set_of_2) {
        applySetOf2UnitLabel(card);
      }
      card.setAttribute('data-theme-card-enriched', '1');
    } finally {
      // Allow MutationObserver to settle before accepting new mutations.
      queueMicrotask(() => {
        isApplying = false;
      });
    }
  };

  /** Instant pass for handles already fetched (stops autocomplete Set of 2 flicker). */
  const processCachedCards = (root = document) => {
    for (const card of root.querySelectorAll(CARD_SELECTORS)) {
      const handle = getHandleFromCard(card);
      if (!handle || !resolvedFlags.has(handle)) continue;
      applyFlagsToCard(card, resolvedFlags.get(handle));
    }
  };

  const processMissingCards = async (root = document) => {
    const cards = [...root.querySelectorAll(CARD_SELECTORS)];
    const jobs = [];

    for (const card of cards) {
      const handle = getHandleFromCard(card);
      if (!handle) continue;
      if (resolvedFlags.has(handle)) {
        applyFlagsToCard(card, resolvedFlags.get(handle));
        continue;
      }
      jobs.push(
        fetchProductFlags(handle).then((flags) => {
          // Card may have been replaced by Graphene while fetching — re-query by handle.
          const liveCards = [...(root.querySelectorAll?.(CARD_SELECTORS) || document.querySelectorAll(CARD_SELECTORS))];
          for (const live of liveCards) {
            if (getHandleFromCard(live) === handle) applyFlagsToCard(live, flags);
          }
        })
      );
    }

    await Promise.all(jobs);
  };

  const scheduleProcess = (root) => {
    // Warm cache: paint Set of 2 / low-stock in the same turn as Graphene DOM replace.
    processCachedCards(root || document);

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      processMissingCards(root || document);
    }, 40);
  };

  const mutationsAreThemeOnly = (mutations) =>
    mutations.every((mutation) => {
      if (mutation.type !== 'childList') return false;
      const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
      if (!nodes.length) return true;
      return nodes.every((node) => {
        if (node.nodeType !== 1) return true;
        return node.matches?.(THEME_NODE_SELECTOR) || node.closest?.(THEME_NODE_SELECTOR);
      });
    });

  window.applyLowStockProductCards = (root) => scheduleProcess(root);

  document.addEventListener('swym:collections-loaded', () => scheduleProcess(document));
  document.addEventListener('DOMContentLoaded', () => {
    for (const selector of OBSERVE_SELECTORS) {
      const target = document.querySelector(selector);
      if (!target) continue;
      new MutationObserver((mutations) => {
        if (isApplying || mutationsAreThemeOnly(mutations)) return;
        scheduleProcess(target);
      }).observe(target, {
        childList: true,
        subtree: true,
      });
    }
    scheduleProcess(document);
  });

  if (document.readyState !== 'loading') scheduleProcess(document);
})();
